import React, { useEffect, useState } from 'react'
import { TitleBar } from '../../common'
import { TITLES, ROUTES, LOCAL_STORAGE } from '../../../constants'
import { manufacturingLogsData } from '../../../service/logs'
import { PaginatedTable } from '../../common'
import { LOGS_COLUMN } from '../../../constants'
import { logsDataCompiler } from '../../../utils/dataCompiler/logsDataCompiler'
import { SearchBar } from '../../common'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../../shared'
import { BUTTON } from '../../../constants'
import { DropDown } from '../../common'
import { getLocalStorageItem } from '../../../utils'
import DateTimeRangePicker from '../../common/DateTimeRangePicker'

export const LogsContent = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [logsData, setLogsData] = useState([]);
    const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
    const [paginationData, setPaginationData] = useState({});
    const [filteredColumn, setFilteredColumn] = useState(LOGS_COLUMN);
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [search, setSearch] = useState(false);
    const [sort1, setSort1] = useState(searchParams.get('filter') || LOGS_COLUMN[8].name);
    const [sortingArray1, setSortingArray1] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDateRange, setShowDateRange] = useState(false);
    const [startDate, setStartDate] = useState(searchParams.get('start_date') || '');
    const [endDate, setEndDate] = useState(searchParams.get('end_date') || '');

    const updateUrlParams = (params = {}) => {
        const newParams = {};

        // Only include non-empty values
        if (params.search) newParams.search = params.search;
        // Only include filter if it's not the default value
        if (params.filter && params.filter !== LOGS_COLUMN[8].name) newParams.filter = params.filter;
        if (params.page) newParams.page = params.page.toString();
        if (params.start_date) newParams.start_date = params.start_date;
        if (params.end_date) newParams.end_date = params.end_date;

        setSearchParams(newParams, { replace: true });
    };

    useEffect(() => {
        if (startDate || endDate) {
            setShowDateRange(true);
        }
    }, [])

    useEffect(() => {
        const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);

        if (vendorType === 'fabric') {
            const columns = filteredColumn.filter((col) => col.name !== 'Total Qty Produced');
            setFilteredColumn(columns);
        }
        else if (vendorType === 'dyeing') {
            const columns = filteredColumn.filter(
                (col) => col.name !== 'Total Body Produced' && col.name !== 'Total Rib Produced'
            );
            setFilteredColumn(columns);
        }
    }, []);


    const fetchLogsData = async (params = {}) => {
        try {
            setIsLoading(true);
            const response = await manufacturingLogsData({ data: params });
            if (response?.data?.records?.length > 0) {
                setLogsData(response?.data?.records);
                setPaginationData(response.data.params);
                const name = LOGS_COLUMN[8].dataField[0];
                if (response?.data[name] && Array.isArray(response?.data[name])) {
                    setSortingArray1(response.data[name]);
                }
                setIsLoading(false);
            } else {
                setLogsData([]);
                setIsLoading(false);
            }
        } catch (err) {
            setIsLoading(false);
            console.error(err);
        }
    }

    const getLogsData = (additionalParams = {}) => {
        setIsLoading(true);

        const sortSelection = {};
        if (sort1 !== LOGS_COLUMN[8].name) {
            sortSelection.status = sort1.toLowerCase();
        }

        const params = {
            page_number: additionalParams.page_number || pageNumber,
            search: searchTerm ? { invoice_number: searchTerm, 'log_lines.lot': searchTerm, id: searchTerm } : {},
            sort: {
                selection: Object.keys(sortSelection).length > 0 ? sortSelection : {},
            },
            start_date: additionalParams.start_date || startDate || undefined,
            end_date: additionalParams.end_date || endDate || undefined,
        };

        updateUrlParams({
            search: searchTerm,
            filter: sort1 !== LOGS_COLUMN[8].name ? sort1 : '',
            page: params.page_number,
            start_date: params.start_date,
            end_date: params.end_date,
        });

        fetchLogsData(params);
    };

    useEffect(() => {
        const urlSearchTerm = searchParams.get('search');
        const urlFilter = searchParams.get('filter');
        const urlPage = searchParams.get('page');
        const urlStart = searchParams.get('start_date');
        const urlEnd = searchParams.get('end_date');

        let hasChanges = false;

        if (urlSearchTerm && urlSearchTerm !== searchTerm) {
            setSearchTerm(urlSearchTerm);
            hasChanges = true;
        }

        if (urlFilter && urlFilter !== sort1) {
            setSort1(urlFilter);
            hasChanges = true;
        }

        if (urlStart && urlStart !== startDate) {
            setStartDate(urlStart);
            hasChanges = true;
        }

        if (urlEnd && urlEnd !== endDate) {
            setEndDate(urlEnd);
            hasChanges = true;
        }

        if (urlPage && parseInt(urlPage) !== pageNumber) {
            setPageNumber(parseInt(urlPage));
            hasChanges = true;
        }

        // ✅ Always fetch with date range if it exists
        if (!hasChanges || urlStart || urlEnd) {
            getLogsData({
                start_date: urlStart,
                end_date: urlEnd,
            });
        }
    }, []);

    useEffect(() => {
        if (search) {
            setPageNumber(1);
            getLogsData({ page_number: 1 });
        }
    }, [search]);

    useEffect(() => {
        // Only fetch when pageNumber changes and it's not the initial load
        const currentUrlPage = searchParams.get('page');
        if (currentUrlPage && currentUrlPage !== pageNumber.toString() && pageNumber !== 0) {
            getLogsData();
        }
    }, [pageNumber]);

    useEffect(() => {
        // Check if sort1 has changed from its initial value
        const initialSort1 = searchParams.get('filter') || LOGS_COLUMN[8].name;

        // Only trigger a new API call if the value has actually changed
        if (sort1 !== initialSort1) {
            setPageNumber(1); // Reset page number on sorting change
            getLogsData({ page_number: 1 });
        }
    }, [sort1]);

    const resetFilter = () => {
        setSort1(LOGS_COLUMN[8].name);
        setPageNumber(1);
        setStartDate('');
        setEndDate('');
        setShowDateRange(false);

        const cleanParams = {};
        if (searchTerm) cleanParams.search = searchTerm;
        cleanParams.page = '1';

        setSearchParams(cleanParams, { replace: true });

        fetchLogsData({
            page_number: 1,
            search: searchTerm ? { invoice_number: searchTerm } : {},
            sort: { selection: {} },
        });
    };

    useEffect(() => {
        if (!searchTerm && search) {
            setPageNumber(1);
            setSearch(false);

            // Update URL params - remove search but keep filter
            updateUrlParams({
                filter: sort1 !== LOGS_COLUMN[8]?.name ? sort1 : '',
                page: '1',
            });

            getLogsData({ page_number: 1 });
        }
    }, [searchTerm]);

    const handleApply = ({ start, end }) => {
        if (!start || !end) return;

        setStartDate(start);
        setEndDate(end);

        updateUrlParams({
            search: searchTerm,
            filter: sort1 !== LOGS_COLUMN[8].name ? sort1 : '',
            page: '1',
            start_date: start,
            end_date: end,
        });

        getLogsData({
            page_number: 1,
            start_date: start,
            end_date: end,
        });
    };

    return (
        <>
            <TitleBar title={[{ text: TITLES.LOGS, route: ROUTES.LOGS }]} />
            {/* 🔹 Search + Filter + Date Range Row */}
            <div className="d-flex flex-wrap align-items-center gap-3 w-100 mb-3">

                {/* Left side: Search and Filter side-by-side */}
                {/* <div className="d-flex align-items-center flex-wrap gap-3 flex-grow-1"> */}
                <SearchBar
                    search={search}
                    setSearchTerm={setSearchTerm}
                    searchTerm={searchTerm}
                    setSearch={setSearch}
                    onDefaultSearch={getLogsData}
                />

                <Button
                    clickHandler={() => setShowDateRange(!showDateRange)}
                    className="blue-btn d-flex align-items-center justify-content-center px-3 mb-3"
                >
                    Apply Date Range
                </Button>

                <div className="d-flex align-items-center gap-2 mb-3">
                    <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={setSort1} />
                    {(sort1 !== LOGS_COLUMN[8].name || startDate || endDate) && (
                        <Button
                            clickHandler={resetFilter}
                            className="blue-btn d-flex align-items-center justify-content-center px-3"
                        >
                            {BUTTON.CLEAR_FILTERS}
                        </Button>
                    )}
                </div>
            </div>

            {/* 🔹 Animated Collapsible Date Range Picker */}
            <div
                className={`collapse ${showDateRange ? 'show' : ''} mb-3`}
                style={{ transition: 'height 0.9s ease' }}
            >
                <div className="p-3 border rounded bg-white shadow-sm">
                    <DateTimeRangePicker onApply={handleApply} startDateTime={startDate} endDateTime={endDate} />
                </div>
            </div>
            {Array.isArray(logsData) ? (
                <PaginatedTable
                    columns={filteredColumn}
                    initialData={logsData}
                    colFilter={true}
                    to={ROUTES.LOG_DETAILS}
                    compiler={{ use: true, tool: logsDataCompiler }}
                    className="table-4"
                    pageNumber={pageNumber}
                    setPageNumber={setPageNumber}
                    paginationData={paginationData}
                    isLoading={isLoading}
                />
            ) : (
                <div>
                    <h4 className="text-center px-1 py-5">No Records Found</h4>
                </div>
            )}
        </>
    )
}